/**
 * Top filter — the disease × type chip bar used inside DailyView only.
 *
 * Two rows of pill buttons; "全部" on each row resets that axis. The
 * selection callback receives `{disease, type}` with each axis either
 * set to its concrete key or `null` for the "全部" position.
 *
 * This component only owns the chip rendering and the click → callback
 * wiring. Daily view owns the actual filter logic over the fetched
 * payload — Round A wires the callback; Round B can swap in real disease
 * mapping once `/api/daily/:date` exposes a `diseases: DiseaseKey[]`
 * field on each `DailyListItem`.
 */

import { el } from "../dom";
import type { DiseaseKey } from "../types";

export type DiseaseSelection = DiseaseKey | null;
export type TypeSelection = "临床研究" | "人工智能/机器学习相关研究" | "其他基础研究" | null;

export interface TopFilterSelection {
  disease: DiseaseSelection;
  type: TypeSelection;
}

export interface TopFilterOptions {
  initial?: TopFilterSelection;
  onChange: (next: TopFilterSelection) => void;
}

interface DiseaseChip {
  key: DiseaseSelection;
  label: string;
  /** Long-form tooltip text shown via `title`. */
  hint?: string;
}

interface TypeChip {
  key: TypeSelection;
  label: string;
}

const DISEASE_CHIPS: readonly DiseaseChip[] = [
  { key: null, label: "全部" },
  { key: "lung_cancer", label: "肺癌" },
  { key: "mediastinal_tumor", label: "纵隔肿瘤" },
  { key: "esophageal_cancer", label: "食管癌" },
  {
    key: "pneumothorax",
    label: "气胸（含胸部外伤、肋骨骨折、胸壁畸形）",
    hint: "气胸、胸部外伤、肋骨骨折、胸壁畸形",
  },
];

const TYPE_CHIPS: readonly TypeChip[] = [
  { key: null, label: "全部" },
  { key: "临床研究", label: "临床研究" },
  { key: "人工智能/机器学习相关研究", label: "人工智能/机器学习相关研究" },
  { key: "其他基础研究", label: "其他基础研究" },
];

export function topFilter({ initial, onChange }: TopFilterOptions): HTMLElement {
  let state: TopFilterSelection = {
    disease: initial?.disease ?? null,
    type: initial?.type ?? null,
  };

  const update = (next: TopFilterSelection): void => {
    state = next;
    sync();
    onChange(state);
  };

  const root = el("div", { class: "top-filter", role: "toolbar", "aria-label": "文献筛选" }, [
    renderRow("疾病", DISEASE_CHIPS, "disease"),
    renderRow("类型", TYPE_CHIPS, "type"),
  ]);

  function renderRow<R, K>(
    label: string,
    chips: ReadonlyArray<{ key: R | null; label: string; hint?: string }>,
    axis: "disease" | "type",
  ): HTMLElement {
    const axisRoot = el(
      "div",
      {
        class: "top-filter-row",
        role: "group",
        "aria-label": label,
        "data-axis": axis,
      },
      [
        el("span", { class: "top-filter-label" }, label),
        ...chips.map((chip) => buildChip(chip, axis)),
      ],
    );

    function buildChip(
      c: { key: R | null; label: string; hint?: string },
      axisInner: "disease" | "type",
    ): HTMLElement {
      const btn = el(
        "button",
        {
          class: "chip",
          type: "button",
          "data-key": c.key === null ? "" : String(c.key),
          title: c.hint ?? c.label,
          "aria-pressed": "false",
          onclick: () => {
            const next: TopFilterSelection =
              axisInner === "disease"
                ? { ...state, disease: c.key as DiseaseSelection }
                : { ...state, type: c.key as TypeSelection };
            update(next);
          },
        },
        c.label,
      );
      return btn;
    }

    return axisRoot;
  }

  function sync(): void {
    const diseaseButtons = root.querySelectorAll('[data-axis="disease"] .chip');
    diseaseButtons.forEach((btn) => {
      const key = (btn as HTMLElement).dataset["key"];
      const isActive = (state.disease ?? "") === (key ?? "");
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
    const typeButtons = root.querySelectorAll('[data-axis="type"] .chip');
    typeButtons.forEach((btn) => {
      const key = (btn as HTMLElement).dataset["key"];
      const isActive = (state.type ?? "") === (key ?? "");
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  // Initial paint — the constructor wired default-active if `initial`
  // had keys; reflect that in the DOM.
  sync();

  // Expose a tiny imperative API for tests + views that want to read
  // the current selection without subscribing.
  (root as HTMLElement & { __getSelection?: () => TopFilterSelection }).__getSelection =
    () => ({ ...state });

  return root;
}
